import React, { useCallback, useEffect, useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import ClientSearchSelect from '../../components/ao/ClientSearchSelect';
import aoAPI from '../../api/aoAPI';

const PAYMENT_METHOD_TABS = [
  { value: 'Cash',         label: 'Cash' },
  { value: 'GCash',        label: 'GCash' },
  { value: 'BankTransfer', label: 'Bank Transfer' },
];

const MONTHS = [
  { value: 1, label: 'January' },   { value: 2, label: 'February' },
  { value: 3, label: 'March' },     { value: 4, label: 'April' },
  { value: 5, label: 'May' },       { value: 6, label: 'June' },
  { value: 7, label: 'July' },      { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const fmt = (n) =>
  n ? new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2 }).format(n) : '—';

const EMPTY_FORM = {
  date: new Date().toISOString().split('T')[0],
  paymentMethod: 'Cash',
  amount: '',
  referenceNo: '',
  remarks: '',
};

const AOCollectionScreen = () => {
  const [clients, setClients] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [fyFilter, setFyFilter] = useState(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState(new Date().getMonth() + 1);

  const [collections, setCollections] = useState([]);
  const [arBalance, setARBalance] = useState({ totalDisbursed: 0, totalCollected: 0, arBalance: 0 });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');

  // Load clients and staff once
  useEffect(() => {
    const load = async () => {
      try {
        const [clientRes, staffRes] = await Promise.all([
          aoAPI.getClients(),
          aoAPI.getStaff(),
        ]);
        setClients(clientRes.data || []);
        setStaffList(staffRes.data || []);
      } catch (err) {
        console.error('Failed to load clients/staff', err);
      }
    };
    load();
  }, []);

  // Load collections whenever filters change
  const loadCollections = useCallback(async () => {
    if (!selectedClient) {
      setCollections([]);
      setARBalance({ totalDisbursed: 0, totalCollected: 0, arBalance: 0 });
      return;
    }

    setLoading(true);
    try {
      const [collRes, arRes] = await Promise.all([
        aoAPI.getCollections({ clientId: selectedClient, fiscalYear: fyFilter, month: monthFilter }),
        aoAPI.getARSummary({ fiscalYear: fyFilter }),
      ]);

      setCollections(collRes.data || []);

      // Find AR for this client
      const clientAR = (arRes.data || []).find(r => r.client === selectedClient);
      if (clientAR) {
        setARBalance(clientAR);
      } else {
        setARBalance({ totalDisbursed: 0, totalCollected: 0, arBalance: 0 });
      }
    } catch (err) {
      console.error('Failed to load collections', err);
    } finally {
      setLoading(false);
    }
  }, [selectedClient, fyFilter, monthFilter]);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedClient || !selectedStaff || !form.amount) {
      alert('Please fill in required fields');
      return;
    }

    setSaving(true);
    setSaveMsg('');

    try {
      const payload = {
        client: selectedClient,
        staff: selectedStaff,
        date: form.date,
        fiscalYear: fyFilter,
        month: monthFilter,
        amount: parseFloat(form.amount),
        paymentMethod: paymentMethod,
        referenceNo: form.referenceNo,
        remarks: form.remarks,
      };

      if (editId) {
        await aoAPI.updateCollection(editId, payload);
        setSaveMsg('Collection updated');
      } else {
        await aoAPI.createCollection(payload);
        setSaveMsg('Collection created');
      }

      setForm(EMPTY_FORM);
      setEditId(null);
      await loadCollections();

      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveMsg('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record) => {
    setEditId(record._id);
    setPaymentMethod(record.paymentMethod);
    setForm({
      date: record.date.split('T')[0],
      amount: record.amount,
      referenceNo: record.referenceNo,
      remarks: record.remarks,
      paymentMethod: record.paymentMethod,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this collection?')) return;
    try {
      await aoAPI.deleteCollection(id);
      await loadCollections();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  return (
    <Layout pageTitle="Collection Entry">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        
        {/* AR Balance Card */}
        <div className="rounded-xl border border-teal-200 bg-teal-50 px-6 py-4">
          <p className="text-xs text-teal-600 font-medium mb-2">AR Balance</p>
          <div className="flex gap-6 text-sm">
            <span className="text-gray-600">
              Disbursed: <strong className="text-teal-700">₱{fmt(arBalance.totalDisbursed)}</strong>
            </span>
            <span className="text-gray-600">
              Collected: <strong className="text-teal-700">₱{fmt(arBalance.totalCollected)}</strong>
            </span>
            <span className={arBalance.arBalance > 0 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
              Balance: ₱{fmt(arBalance.arBalance)}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Client Selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Client</label>
              <ClientSearchSelect
                clients={clients}
                value={selectedClient}
                onChange={setSelectedClient}
                placeholder="— Select client —"
              />
            </div>

            {/* Fiscal Year */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Fiscal Year</label>
              <input
                type="number"
                value={fyFilter}
                onChange={(e) => setFyFilter(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            {/* Staff Selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Staff</label>
              <select
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">— Select staff —</option>
                {staffList.map(s => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Month</label>
              <select
                value={monthFilter}
                onChange={(e) => setMonthFilter(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {MONTHS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Entry Form */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <h3 className="font-semibold text-gray-700 text-sm mb-4">
            {editId ? 'Edit Collection' : 'New Collection'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Date */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Amount (₱)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* Reference No */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Reference No</label>
                <input
                  type="text"
                  value={form.referenceNo}
                  onChange={(e) => setForm({ ...form, referenceNo: e.target.value })}
                  placeholder="E.g., GCash ref, bank ref"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* Payment Method Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex gap-2">
                {PAYMENT_METHOD_TABS.map(method => (
                  <button
                    key={method.value}
                    type="button"
                    onClick={() => setPaymentMethod(method.value)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                      paymentMethod === method.value
                        ? 'text-teal-600 border-teal-600'
                        : 'text-gray-600 border-transparent hover:text-gray-800'
                    }`}
                  >
                    {method.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Remarks</label>
              <input
                type="text"
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                placeholder="Optional notes"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <div className="flex gap-2 justify-between pt-2">
              <div>
                {saveMsg && (
                  <p className={saveMsg.includes('Error') ? 'text-red-600 text-sm' : 'text-green-600 text-sm'}>
                    {saveMsg}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {editId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditId(null);
                      setForm(EMPTY_FORM);
                      setPaymentMethod('Cash');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editId ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Collection List */}
        {!loading && collections.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left font-medium text-gray-600">Date</th>
                  <th className="px-6 py-3 text-right font-medium text-gray-600">Amount</th>
                  <th className="px-6 py-3 text-left font-medium text-gray-600">Method</th>
                  <th className="px-6 py-3 text-left font-medium text-gray-600">Reference No</th>
                  <th className="px-6 py-3 text-right font-medium text-gray-600">AR Before</th>
                  <th className="px-6 py-3 text-right font-medium text-gray-600">AR After</th>
                  <th className="px-6 py-3 text-left font-medium text-gray-600">Remarks</th>
                  <th className="px-6 py-3 text-right font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {collections.map(record => (
                  <tr key={record._id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-6 py-3 text-gray-700">
                      {new Date(record.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-3 text-right font-medium text-gray-700">
                      ₱{fmt(record.amount)}
                    </td>
                    <td className="px-6 py-3">
                      <span className="inline-block text-xs font-medium px-2 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                        {record.paymentMethod}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-gray-600 text-xs">{record.referenceNo || '—'}</td>
                    <td className="px-6 py-3 text-right text-gray-700">
                      ₱{fmt(record.arBalanceBefore)}
                    </td>
                    <td className="px-6 py-3 text-right text-gray-700">
                      ₱{fmt(record.arBalanceAfter)}
                    </td>
                    <td className="px-6 py-3 text-gray-600 text-xs">{record.remarks || '—'}</td>
                    <td className="px-6 py-3 flex gap-2 justify-end">
                      <button
                        onClick={() => handleEdit(record)}
                        className="text-teal-600 hover:text-teal-700"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(record._id)}
                        className="text-red-600 hover:text-red-700"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && selectedClient && collections.length === 0 && (
          <div className="text-center py-12 text-gray-500">No collections for this period.</div>
        )}

        {loading && (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        )}
      </div>
    </Layout>
  );
};

export default AOCollectionScreen;
